import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { io } from "socket.io-client";
import apiService from "../Services/api.service";
import Title from "../components/PageTitle";

const SupportChat = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { ticketId, doctor, clinic } = location.state || {};
  const token = localStorage.getItem("token");

  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageText, setMessageText] = useState("");
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef(null);

  // 🔌 Initialize socket inside effect — not globally
  useEffect(() => {
    if (!ticketId) return;

    const socket = io(process.env.REACT_APP_API_HOST_URL, {
      transports: ["websocket"], // prefer WebSocket over polling
      reconnectionAttempts: 3, // optional: limit retries
      autoConnect: true,
    });

    // Join chat room for this ticket
    socket.emit("joinTicket", ticketId);

    // Listen for live messages
    socket.on("newMessage", (newMsg) => {
      setMessages((prev) => [...prev, newMsg]);
    });

    // Cleanup: remove listener + disconnect socket
    return () => {
      socket.off("newMessage");
      socket.disconnect();
    };
  }, [ticketId]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Fetch chat data (initial load)
  const fetchChat = async () => {
    try {
      const data = await apiService.fetchChat(ticketId, token);
      if (data.success) {
        setTicket(data.ticket);
        setMessages(data.messages || []);
      } else {
        setTicket(null);
      }
    } catch (err) {
      console.error("Failed to fetch chat:", err);
      setTicket(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (ticketId) fetchChat();
  }, [ticketId]);

  // Handle message send
  const handleSend = async () => {
    if (!messageText.trim()) return;
    const msg = messageText.trim();
    setMessageText("");
    await apiService.sendMessage(ticketId, msg, token);
    // No manual fetch — server emits "newMessage"
  };

  // Loading state
  if (loading)
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );

  // Ticket not found state
  if (!ticket)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
        <Typography color="text.secondary">
          Ticket not found or failed to load.
        </Typography>
      </Box>
    );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        height: "100%",
        overflow: "hidden",
        backgroundColor: theme.palette.mode === "dark" ? "#333" : "#f9f9f9",
      }}
    >
      {/* Title */}
      <Title text="Chat" />

      {/* Header */}
      <Box
        sx={{
          flexShrink: 0,
          p: 2,
          borderBottom: "1px solid #E5E7EB",
          backgroundColor: theme.palette.mode === "dark" ? "#333" : "#f9f9f9",
          boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
          zIndex: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
          <IconButton onClick={() => navigate("/dashboard/support")}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {ticket.subject}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Ticket ID: {ticket.ticketId}
            </Typography>
          </Box>
        </Box>

        <Stack direction="row" alignItems="center" spacing={2} sx={{ mt: 1 }}>
          <Avatar
            sx={{
              width: 48,
              height: 48,
              bgcolor:
                theme.palette.mode === "dark"
                  ? theme.palette.primary.dark
                  : theme.palette.primary.main,
              fontWeight: 600,
              fontSize: "1rem",
            }}
          >
            {doctor ? doctor.charAt(0).toUpperCase() : "D"}
          </Avatar>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {doctor || "Doctor Name"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {clinic || "Clinic Name"}
            </Typography>
          </Box>
          <Chip
            label={ticket.status?.toUpperCase() || "OPEN"}
            color={
              ticket.status === "open"
                ? "success"
                : ticket.status === "closed"
                ? "default"
                : "warning"
            }
            size="small"
            sx={{ ml: "auto" }}
          />
        </Stack>
      </Box>

      {/* Messages */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          p: 3,
          display: "flex",
          flexDirection: "column",
          gap: 2,
          backgroundColor: theme.palette.mode === "dark" ? "#333" : "#f9f9f9",
        }}
      >
        {messages.map((msg) => {
          const isSupport = msg.senderType === "support";
          return (
            <Box
              key={msg._id}
              sx={{
                display: "flex",
                alignItems: "flex-end",
                justifyContent: isSupport ? "flex-end" : "flex-start",
                gap: 1,
              }}
            >
              {!isSupport && (
                <Avatar
                  sx={{
                    width: 36,
                    height: 36,
                    fontSize: 14,
                    bgcolor: "#white",
                  }}
                >
                  U
                </Avatar>
              )}

              <Box
                sx={{
                  bgcolor: isSupport
                    ? theme.palette.primary.dark
                    : "white",
                  color: isSupport ? "white" : "black",
                  px: 2,
                  py: 1.2,
                  borderRadius: 3,
                  maxWidth: "65%",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                  borderTopLeftRadius: isSupport ? 12 : 4,
                  borderTopRightRadius: isSupport ? 4 : 12,
                }}
              >
                <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
                  {msg.message}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    textAlign: isSupport ? "right" : "left",
                    opacity: 0.7,
                    mt: 0.4,
                    fontSize: "0.7rem",
                  }}
                >
                  {new Date(msg.createdAt).toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </Typography>
              </Box>

              {isSupport && (
                <Avatar
                  sx={{
                    width: 36,
                    height: 36,
                    fontSize: 14,
                    bgcolor:
                      theme.palette.mode === "dark"
                        ? theme.palette.primary.dark
                        : theme.palette.primary.main,
                  }}
                >
                  S
                </Avatar>
              )}
            </Box>
          );
        })}
        <div ref={messagesEndRef} />
      </Box>

      {/* Input Bar */}
      <Box
        sx={{
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          gap: 1,
          p: 2,
          boxShadow: "0 -2px 6px rgba(0,0,0,0.04)",
          zIndex: 2,
        }}
      >
        <TextField
          fullWidth
          placeholder={
            ticket.status === "closed"
              ? "Cannot send messages. Ticket is closed."
              : "Type your message..."
          }
          value={messageText}
          onChange={(e) => setMessageText(e.target.value)}
          size="small"
          variant="outlined"
          disabled={ticket.status === "closed"}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "999px",
            },
          }}
          onKeyDown={(e) =>
            e.key === "Enter" && ticket.status !== "closed" && handleSend()
          }
        />
        <IconButton
          onClick={handleSend}
          disabled={ticket.status === "closed"}
          sx={{
            bgcolor: ticket.status === "closed" ? "#cbd5e1" : "#2563EB",
            color: "white",
            p: 1.2,
            borderRadius: "50%",
            "&:hover": {
              bgcolor: ticket.status === "closed" ? "#cbd5e1" : "#1E40AF",
            },
          }}
        >
          <SendRoundedIcon />
        </IconButton>
      </Box>
    </Box>
  );
};

export default SupportChat;
